'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, BarChart, AlertCircle, FileSpreadsheet, LineChart, DollarSign } from "lucide-react";
import { useParams } from "next/navigation";
// import { localizeSpelling } from "@/lib/spelling-localization";

// Temporary fallback function
const localizeSpelling = (text: string, locale: string) => text;

const riskAssessmentData = {
  title: "Risk Assessment & Specialized Coverage Models",
  subtitle: "AI-driven risk models using vehicle telemetry and driving pattern analysis for personalized insurance products.",
  headerGradient: "bg-gradient-to-r from-red-500 via-orange-400 to-amber-500",
  accentColor: "text-red-600",
  buttonColor: "bg-red-600 hover:bg-red-700",
  challenge: "Auto insurance providers need more accurate risk assessment tools, efficient claims processing, and data-driven models for usage-based insurance products.",
  solutionHighlights: [
    "AI-driven risk models using vehicle telemetry and driving pattern analysis",
    "Computer vision systems for automated damage assessment and claims processing",
    "Real-time driving behavior monitoring for dynamic premium adjustments",
    "Fraud detection algorithms analyzing patterns across claims data"
  ],
  benefits: [
    "70-75% improvement in risk assessment precision",
    "45-50% faster claims resolution",
    "Creation of personalized insurance products",
    "Advanced analytics enabling data-driven underwriting"
  ],
  techStack: ["Machine Learning", "Predictive Analytics", "Computer Vision", "Telematics", "IoT Sensors", "Big Data"],
  riskStats: {
    riskFactors: "32",
    fraudAlerts: "4",
    predictionAccuracy: "94%",
    processingTime: "1.8s"
  }
};

// Safe localization helper that falls back to original text if localization fails
const safeLocalizeSpelling = (text: string, locale: string) => {
  try {
    return localizeSpelling(text, locale);
  } catch (error) {
    console.warn(`Localization failed for "${text}" in locale "${locale}":`, error);
    return text;
  }
};

export default function RiskAssessmentModelsPage() {
  // Since this is a client component, we use useParams hook
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  // Localize all text content based on current locale
  const localizedData = {
    ...riskAssessmentData,
    title: safeLocalizeSpelling(riskAssessmentData.title, currentLocale),
    subtitle: safeLocalizeSpelling(riskAssessmentData.subtitle, currentLocale),
    challenge: safeLocalizeSpelling(riskAssessmentData.challenge, currentLocale),
    solutionHighlights: riskAssessmentData.solutionHighlights.map(
      highlight => safeLocalizeSpelling(highlight, currentLocale)
    ),
    benefits: riskAssessmentData.benefits.map(
      benefit => safeLocalizeSpelling(benefit, currentLocale)
    ),
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${localizedData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-amber-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              {safeLocalizeSpelling("Back to Portfolio", currentLocale)}
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {localizedData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-orange-100 max-w-3xl mx-auto">
            {localizedData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {localizedData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Risk Assessment Models Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${localizedData.accentColor}`}>
              {safeLocalizeSpelling("AI Risk Modeling Platform", currentLocale)}
            </h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>{safeLocalizeSpelling("Vehicle Risk Profile", currentLocale)}</span>
                <span className="text-xs bg-red-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <BarChart size={12} className="mr-1"/>
                  {safeLocalizeSpelling("Live Analysis", currentLocale)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Risk Assessment Frame */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-white rounded-lg relative overflow-hidden shadow-inner border border-slate-300 flex flex-col items-center justify-center text-xs p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <FileSpreadsheet size={18} className="text-red-500 mr-1" />
                        <span className="text-slate-700 font-medium">{safeLocalizeSpelling("Vehicle #VIN-ABC123", currentLocale)}</span>
                      </div>
                      <div className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                        {safeLocalizeSpelling("High-Risk Zone", currentLocale)}
                      </div>
                    </div>
                    
                    <div className="mt-8 w-full flex flex-col items-center">
                      <div className="relative w-48 h-48 mb-4">
                        {/* Circular progress gauge with high risk styling */}
                        <div className="absolute inset-0 rounded-full border-8 border-slate-100"></div>
                        <div className="absolute inset-0 rounded-full border-8 border-transparent border-t-red-500 border-r-red-500 border-b-orange-400"></div>
                        <div className="absolute inset-0 flex items-center justify-center flex-col">
                          <span className="text-4xl font-bold text-red-600">85</span>
                          <span className="text-sm text-slate-500">{safeLocalizeSpelling("Risk Score", currentLocale)}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 w-full mt-2">
                        <div className="bg-orange-50 p-2 rounded border border-orange-200">
                          <div className="flex items-center">
                            <LineChart size={14} className="text-orange-500 mr-1" />
                            <span className="text-xs text-slate-700">{safeLocalizeSpelling("Accident Probability", currentLocale)}</span>
                          </div>
                          <div className="text-sm font-semibold text-orange-600 mt-1">42%</div>
                        </div>
                        <div className="bg-red-50 p-2 rounded border border-red-200">
                          <div className="flex items-center">
                            <DollarSign size={14} className="text-red-500 mr-1" />
                            <span className="text-xs text-slate-700">{safeLocalizeSpelling("Premium Adjustment", currentLocale)}</span>
                          </div>
                          <div className="text-sm font-semibold text-red-600 mt-1">+15.2%</div>
                        </div>
                      </div>
                      
                      <div className="mt-3 w-full">
                        <div className="bg-amber-50 p-2 rounded border border-amber-200">
                          <div className="flex items-center">
                            <AlertCircle size={14} className="text-amber-600 mr-1" />
                            <span className="text-xs text-amber-700">{safeLocalizeSpelling("Recent Events", currentLocale)}</span>
                          </div>
                          <div className="text-xs text-slate-600 mt-1">{safeLocalizeSpelling("3 hard braking events, 2 speeding incidents", currentLocale)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Risk Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: safeLocalizeSpelling("Risk Factors", currentLocale), value: localizedData.riskStats.riskFactors, color: "text-slate-700", bgColor:"bg-slate-200" }, 
                    { label: safeLocalizeSpelling("Fraud Alerts", currentLocale), value: localizedData.riskStats.fraudAlerts, color: "text-red-700", bgColor:"bg-red-100" }, 
                    { label: safeLocalizeSpelling("Model Accuracy", currentLocale), value: localizedData.riskStats.predictionAccuracy, color: "text-green-700", bgColor:"bg-green-100" }, 
                    { label: safeLocalizeSpelling("Analysis Time", currentLocale), value: localizedData.riskStats.processingTime, color: "text-orange-700", bgColor:"bg-orange-100" } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <AlertCircle size={12} className="text-red-500"/>
                <span>
                  {safeLocalizeSpelling("Status: High-risk profile detected. Telematics monitoring recommended.", currentLocale)}
                </span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("The Challenge", currentLocale)}</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {localizedData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Our Solution", currentLocale)}</h2>
              <ul className="space-y-3 text-slate-700">
                {localizedData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${localizedData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Key Benefits", currentLocale)}</h2>
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {localizedData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start text-red-800">
                      <CheckCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Technologies Used", currentLocale)}</h2>
              <div className="flex flex-wrap gap-3">
                {localizedData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${localizedData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50`}>
              {safeLocalizeSpelling("Optimize Risk Assessment", currentLocale)}
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 